'use client';

import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEventSourceEvent } from '@/shared/hooks/useEventSourceEvent';
import { subscriptionKeys } from '../queryKeys';
import { getAllowanceMeter, type AllowanceMeterData } from '../actions/getAllowanceMeter';

/**
 * The one read of the allowance meter.
 *
 * Five components want this number at once — the header ring, the note on the
 * order screen, the free-try reminder, the "not enough" popup — and each of
 * them used to own a `useState` + `useEffect` + `getAllowanceMeter()`. A page
 * carrying three of them asked the server three times for the same answer, and
 * every remount asked again; the action is not cheap (it reads the allowance
 * summary and prices three features through the accounts price-book, ~700 ms).
 *
 * One query key means one request, however many components observe it, and the
 * answer is reused for `staleTime` instead of refetched per mount.
 */

/**
 * The portal emits `subscription.balance.updated` once per settle, which during
 * a generation run is several times a second. The meter is a percentage and a
 * few "≈ N left" estimates — it does not need that resolution, and refetching
 * at that rate costs more than the number is worth. Refresh at most this often.
 */
const EVENT_REFRESH_THROTTLE_MS = 10_000;
let lastEventRefreshAt = 0;

export interface UseAllowanceMeter {
  data: AllowanceMeterData | null;
  loading: boolean;
  /** Re-read now, ignoring the throttle (for an explicit user action). */
  refresh: () => void;
}

export function useAllowanceMeter(): UseAllowanceMeter {
  const queryClient = useQueryClient();
  const key = subscriptionKeys.allowanceMeter();

  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => getAllowanceMeter(),
    /**
     * Everything that CREDITS the allowance happens in accounts — an admin
     * grant, a purchase, the monthly refill — and none of it emits a portal
     * event. Coming back to the tab is the moment a parent expects to see a
     * top-up they just made, so re-read then. (Overrides the global
     * `refetchOnWindowFocus: false`.)
     *
     * `'always'`, not `true`: a top-up takes less than the stale window, and
     * plain `true` only refetches a STALE query — so a parent who paid and came
     * straight back was shown the balance from before they paid.
     */
    staleTime: 30_000,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
  });

  const refresh = useCallback(() => {
    lastEventRefreshAt = Date.now();
    void queryClient.invalidateQueries({ queryKey: key });
  }, [queryClient, key]);

  useEventSourceEvent(
    'subscription.balance.updated',
    useCallback(() => {
      const now = Date.now();
      if (now - lastEventRefreshAt < EVENT_REFRESH_THROTTLE_MS) return;
      lastEventRefreshAt = now;
      void queryClient.invalidateQueries({ queryKey: key });
    }, [queryClient, key]),
  );

  return { data: data ?? null, loading: isLoading, refresh };
}
